import cn from 'classnames';
import {useEffect, useRef, useState} from 'react';
import defaultImg from '../../../assets/image/newShop/defaultImg.png';
import {BasketResponse} from '../../../services/hookService/getBasket/types';
import BasketItemEditButton from '../components/BasketItemEditButton';
import PriceWithDiscount from '../components/PriceWithDiscount';
import WarningMessage from '../components/WarningMessage';
import RemoveFromBasketModal from './RemoveFromBasketModal';
interface ItemCardProps {
  cardItem: BasketResponse;
}

const BasketItemCard = ({cardItem}: ItemCardProps) => {
  const {quantity, advertisement, guid, is_deleted, discount_price} = cardItem;
  const {
    ad_price,
    id,
    image,
    min_sell_quantity,
    title,
    hold_quantity,
    available_quantity,
  } = advertisement;

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [basketQuantity, setBasketQuantity] = useState(quantity);

  // sync basketQuantity with api quantity
  useEffect(() => {
    if (!quantity) return;
    setBasketQuantity(quantity);
  }, [quantity]);

  const openModal = () => setIsModalOpen(true);

  const trueMaxQuantity = Math.abs(
    (hold_quantity ?? 0) - (available_quantity ?? 0),
  );

  const triggeredMinWarningRef = useRef(false);
  const triggeredMaxWarningRef = useRef(false);

  useEffect(() => {
    // Reset the refs ONCE per render to only show this once
    triggeredMinWarningRef.current = false;
    triggeredMaxWarningRef.current = false;
  }, []);

  useEffect(() => {
    // Trigger min_sell warning only once when the conditions are met
    if (!triggeredMinWarningRef.current && basketQuantity < min_sell_quantity) {
      triggeredMinWarningRef.current = true;
      setBasketQuantity(min_sell_quantity);
    }

    // Trigger max decrease only once when the conditions are met
    if (!triggeredMaxWarningRef.current && trueMaxQuantity < basketQuantity) {
      triggeredMaxWarningRef.current = true;
      setBasketQuantity(trueMaxQuantity);
    }
  }, [basketQuantity, min_sell_quantity, trueMaxQuantity]);

  const showMinQuantityWarning = triggeredMinWarningRef.current;
  const showMaxQuantityWarning = triggeredMaxWarningRef.current;

  const handleDeleteClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    openModal();
  };

  return (
    <div className="flex flex-col gap-2">
      <div className="relative">
        {/* Card */}
        <div
          className={cn(
            'cardItem relative transition-opacity',
            is_deleted && 'pointer-events-none',
          )}
        >
          {/* Dim overlay */}
          {is_deleted && (
            <div className="absolute inset-0 z-10 rounded-lg bg-white/50" />
          )}

          {/* Header */}
          <div className="relative z-0 flex items-center gap-3">
            <div className="relative w-fit shrink-0">
              <img
                src={image ?? defaultImg}
                alt="item-icon"
                className="w-14 h-14"
              />

              <span className="bg-gray-100 text-gray-800 px-[8px] py-[2px] rounded-full text-sm font-medium absolute -top-1 -left-2">
                {basketQuantity}
              </span>
            </div>

            <span className="font-medium text-md line-clamp-1">{title}</span>
          </div>

          {/* Price */}

          <div className="grid grid-cols-8 mt-4 text-sm ">
            <span className="flex items-center col-span-4 gap-2 text-gray-700">
              قیمت کل
            </span>
            <PriceWithDiscount
              className="col-span-4"
              originalPrice={ad_price * quantity}
              discountPrice={discount_price}
            />
          </div>

          {/* Actions */}
          {!is_deleted && (
            <div className="flex items-center justify-between mt-3">
              <button
                type="button"
                className="z-20 flex items-center justify-center gap-1 p-0 text-sm text-red-500 bg-transparent border-none"
                onClick={handleDeleteClick}
              >
                <i className="text-lg pointer-events-none icon-nameDelete" />
                <span className="pointer-events-none">حذف از لیست</span>
              </button>

              <BasketItemEditButton
                variant="sm"
                hideToast
                min_sell_quantity={min_sell_quantity}
                available_quantity={available_quantity}
                hold_quantity={hold_quantity}
                basket_quantity={basketQuantity}
                basket_guid={guid}
                price={ad_price}
                advertisement_id={id}
                shouldRemoveItem={true}
                handleRemovingItem={setIsModalOpen}
              />
            </div>
          )}

          {/* Modal */}
          <RemoveFromBasketModal
            isOpen={isModalOpen}
            setIsOpen={setIsModalOpen}
            basket_guid={guid}
          />
        </div>

        {/* Badge – NOT affected */}
        {is_deleted && (
          <div className="absolute z-20 px-2 py-1 text-sm text-red-500 rounded-lg bg-red-50 top-2 left-2">
            محصول ناموجود
          </div>
        )}
      </div>

      {showMinQuantityWarning && (
        <WarningMessage title="حداقل مقدار قابل خرید این محصول افزایش یافته است" />
      )}

      {showMaxQuantityWarning && (
        <WarningMessage title="موجودی این محصول کاهش یافته است" />
      )}
    </div>
  );
};

export default BasketItemCard;
